import React, { useState, useEffect } from 'react';
import { FormInput } from '../../forms/FormInput';
import { AlertCircle, CheckCircle, Save } from 'lucide-react';

const ConsentForm = ({ onSubmit, initialData = {}, handleFormChange, hasUnsavedChanges, lastAutoSave, sessionData, formType }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    clientName: '',
    counselorName: '',
    counselorPosition: '',
    date: new Date().toISOString().split('T')[0],
    clientSignature: '',
    counselorSignature: '',
    emergencyContact: {
      name: '',
      relationship: '',
      phone: ''
    }
  });

  // Initialize form data from session data
  useEffect(() => {
    if (sessionData?.consent) {
      setFormData(prev => ({
        ...prev,
        ...sessionData.consent,
        date: sessionData.consent.date
          ? new Date(sessionData.consent.date).toISOString().split('T')[0]
          : prev.date,
        emergencyContact: {
          ...prev.emergencyContact,
          ...(sessionData.consent.emergencyContact || {})
        }
      }));
    }
  }, [sessionData?.consent]);

  // Handle form field changes with auto-save
  const handleChange = (field, value) => {
    const updatedData = { ...formData };

    // Handle nested fields (like emergencyContact.name)
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      updatedData[parent] = {
        ...updatedData[parent],
        [child]: value
      };
    } else {
      updatedData[field] = value;
    }

    setFormData(updatedData);

    // Trigger auto-save through the session hook
    if (handleFormChange) {
      handleFormChange(formType, updatedData);
    }
  };

  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
        emergencyContact: {
          ...prev.emergencyContact,
          ...(initialData.emergencyContact || {})
        }
      }));
    }
  }, [initialData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!formData.clientName || !formData.clientSignature || !formData.counselorSignature) {
        throw new Error('Please fill in all required fields and signatures.');
      }

      if (!formData.emergencyContact.name ||
        !formData.emergencyContact.relationship ||
        !formData.emergencyContact.phone) {
        throw new Error('Please provide complete emergency contact information.');
      }
      await onSubmit(formData);
    } catch (err) {
      setError(err.message || 'Failed to submit consent form.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">

      {/* Confidentiality Notice */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
        <div className="flex">
          <AlertCircle className="h-5 w-5 text-blue-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Confidentiality Notice
            </h3>
            <p className="mt-1 text-sm text-blue-700">
              Information shared in counseling sessions is confidential with exceptions
              for risk of harm to self/others or as required by law.
            </p>
          </div>
        </div>
      </div>
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Header with auto-save status */}
      <div className="px-6 py-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">Consent Form</h2>

          {/* Auto-save status indicator */}
          <div className="flex items-center gap-2">
            {hasUnsavedChanges && (
              <div className="bg-amber-50 px-3 py-1 rounded text-amber-700 flex items-center gap-1">
                <Save className="h-4 w-4 animate-pulse" />
                <span className="text-sm">Auto-saving...</span>
              </div>
            )}

            {lastAutoSave && !hasUnsavedChanges && (
              <div className="bg-green-50 px-3 py-1 rounded text-green-700 flex items-center gap-1">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm">Saved {new Date(lastAutoSave).toLocaleTimeString()}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Counselors Consent Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Counselors Consent Section</h3>
          <p className="text-sm text-gray-700">
            The Counseling Center maintains a strict level of client confidentiality. This means
            that individuals using any of the services offered by the Center can expect that their
            privacy will be respected. They can trust that their information will not be discussed
            or disclosed to anyone outside the Counseling Center.
          </p>
          <p className="text-sm text-gray-700">
            All Counseling Center staff members or those individuals working in the center are
            therefore required to acknowledge and commit to maintaining the highest standard of
            confidentiality. It is understood that continued employment with the Counseling Center
            is based on maintaining this standard. Anyone who breaks confidentiality will be
            immediately dismissed.
          </p>
          <p className="text-sm text-gray-700">
            I have read and understand the above; I understand the requirements of continued
            employment at the Counseling Center.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              label="Name"
              value={formData.counselorName}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  counselorName: e.target.value,
                }))
              }
              required
            />
            <FormInput
              label="Position"
              value={formData.counselorPosition}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  counselorPosition: e.target.value,
                }))
              }
              required
            />
          </div>
          <FormInput
            label="Signature"
            placeholder="Type your full name to sign"
            value={formData.counselorSignature}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                counselorSignature: e.target.value,
              }))
            }
            required
          />
          <FormInput
            type="date"
            label="Date"
            value={formData.signedAt}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              signedAt: e.target.value
            }))}
            required
          />
        </div>

        {/* Client Consent Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Client Consent Section</h3>
          <p className="text-sm text-gray-700">
            I __________ (client) give consent for receiving counseling services. My Counselor has
            explained to me the counseling process, and I understand that therapeutic counseling
            requires my active involvement, including my effort to change or undertake tasks given
            to me during and in between sessions. I also understand that the issues discussed in
            the counseling process will remain confidential and that the oath of confidentiality
            will be broken with my permission or if I plan to harm myself or another person. When
            counseling is mandated by a referral source, I understand that the counselor will
            provide the required information on my attendance and progress in counseling, and in
            case a psychological diagnosis is required, the counselor will inform me and give me
            information with my full knowledge and permission.
          </p>
          <p className="text-sm text-gray-700">
            If you have any questions, please feel free to ask. Kindly sign and date this form.
          </p>

          <FormInput
            label="Client Name"
            value={formData.clientName}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                clientName: e.target.value,
              }))
            }
            required
          />
          <FormInput
            label="Client Signature"
            placeholder="Type your full name to sign"
            value={formData.clientSignature}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                clientSignature: e.target.value,
              }))
            }
            required
          />
        </div>

        {/* Emergency Contact Section */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-900">Emergency Contact</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormInput
              label="Name"
              value={formData.emergencyContact.name}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  emergencyContact: {
                    ...prev.emergencyContact,
                    name: e.target.value,
                  },
                }))
              }
              required
            />
            <FormInput
              label="Relationship"
              value={formData.emergencyContact.relationship}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  emergencyContact: {
                    ...prev.emergencyContact,
                    relationship: e.target.value,
                  },
                }))
              }
              required
            />
            <FormInput
              label="Phone Number"
              value={formData.emergencyContact.phone}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  emergencyContact: {
                    ...prev.emergencyContact,
                    phone: e.target.value,
                  },
                }))
              }
              required
            />
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-300"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Submitting...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Submit Consent Form
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ConsentForm;
